/**
 * Offline Store — IndexedDB persistence for PWA form features
 * 
 * Two object stores:
 *   1. "drafts"  — auto-saved form state for resume-on-return
 *   2. "outbox"  — queued form submissions waiting for connectivity
 * 
 * Uses raw IndexedDB (no library) to keep bundle size zero.
 */

const DB_NAME = 'jdrivero-offline'
const DB_VERSION = 3
const STORE_DRAFTS = 'drafts'
const STORE_OUTBOX = 'outbox'
const STORE_REFERENCE = 'reference_cache'

// ─── Database ──────────────────────────────────────────────────────────────

let dbPromise = null

function openDB() {
    if (dbPromise) return dbPromise
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION)
        req.onupgradeneeded = (e) => {
            const db = e.target.result
            // Drafts: keyed by formType (e.g. "construction_new_project")
            if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
                db.createObjectStore(STORE_DRAFTS, { keyPath: 'formType' })
            }
            // Outbox: auto-increment ID, each entry is a full submission payload
            if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
                const outbox = db.createObjectStore(STORE_OUTBOX, { keyPath: 'id', autoIncrement: true })
                outbox.createIndex('status', 'status', { unique: false })
            }
            // Reference cache: keyed by "{userId}:{tableName}" for per-user per-table caching
            if (!db.objectStoreNames.contains(STORE_REFERENCE)) {
                db.createObjectStore(STORE_REFERENCE, { keyPath: 'key' })
            }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })
    return dbPromise
}

function tx(storeName, mode = 'readonly') {
    return openDB().then(db => {
        const transaction = db.transaction(storeName, mode)
        return transaction.objectStore(storeName)
    })
}

// ─── Drafts ────────────────────────────────────────────────────────────────

/**
 * Build a session-scoped key so different users never see each other's drafts.
 * Admin: "{userId}:{formType}"   Public/anonymous: "public:{formType}"
 */
function draftKey(formType, userId) {
    return userId ? `${userId}:${formType}` : `public:${formType}`
}

/**
 * Save a form draft. Files are stored as { name, type, size, data: ArrayBuffer }.
 * @param {string} formType — e.g. "construction_new_project"
 * @param {object} formData — current field values
 * @param {File[]} files — attached files (will be serialized to ArrayBuffer)
 * @param {string|null} userId — Supabase user ID (null for public/anonymous)
 * @param {object|null} ui — optional UI state (e.g. catalog filters, panel toggles)
 */
export async function saveDraft(formType, formData, files = [], userId = null, ui = null) {
    const serializedFiles = await Promise.all(
        files.map(async (file) => ({
            name: file.name,
            type: file.type,
            size: file.size,
            data: await file.arrayBuffer()
        }))
    )
    const key = draftKey(formType, userId)
    const store = await tx(STORE_DRAFTS, 'readwrite')
    return new Promise((resolve, reject) => {
        const req = store.put({
            formType: key,
            formData,
            files: serializedFiles,
            userId: userId || null,
            ui: ui || null,
            savedAt: new Date().toISOString()
        })
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
    })
}

/**
 * Load a saved draft. Returns null if none exists.
 * Files are returned as File objects (reconstructed from ArrayBuffer).
 * @param {string|null} userId — Supabase user ID (null for public/anonymous)
 */
export async function loadDraft(formType, userId = null) {
    const key = draftKey(formType, userId)
    const store = await tx(STORE_DRAFTS)
    return new Promise((resolve, reject) => {
        const req = store.get(key)
        req.onsuccess = () => {
            const result = req.result
            if (!result) return resolve(null)
            // Reconstruct File objects from stored ArrayBuffers
            const files = (result.files || []).map(f =>
                new File([f.data], f.name, { type: f.type })
            )
            resolve({ formData: result.formData, files, savedAt: result.savedAt, ui: result.ui || null })
        }
        req.onerror = () => reject(req.error)
    })
}

/**
 * Delete a draft (e.g. after successful submission).
 * @param {string|null} userId — Supabase user ID (null for public/anonymous)
 */
export async function deleteDraft(formType, userId = null) {
    const key = draftKey(formType, userId)
    const store = await tx(STORE_DRAFTS, 'readwrite')
    return new Promise((resolve, reject) => {
        const req = store.delete(key)
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
    })
}

/**
 * List all saved drafts (for a "resume" UI).
 */
export async function listDrafts() {
    const store = await tx(STORE_DRAFTS)
    return new Promise((resolve, reject) => {
        const req = store.getAll()
        req.onsuccess = () => resolve(req.result || [])
        req.onerror = () => reject(req.error)
    })
}

// ─── Outbox (offline submission queue) ─────────────────────────────────────

/**
 * Queue a form submission for later sync.
 * @param {object} payload — full submission data:
 *   { formType, formConfig, formData, columnValues, itemName, files[] }
 *   Files are serialized to ArrayBuffer for IndexedDB storage.
 * @returns {number} — the outbox entry ID
 */
export async function addToOutbox(payload) {
    const serializedFiles = await Promise.all(
        (payload.files || []).map(async (file) => ({
            name: file.name,
            type: file.type,
            size: file.size,
            data: await file.arrayBuffer()
        }))
    )
    const store = await tx(STORE_OUTBOX, 'readwrite')
    return new Promise((resolve, reject) => {
        const req = store.add({
            ...payload,
            files: serializedFiles,
            status: 'pending',
            queuedAt: new Date().toISOString(),
            attempts: 0,
            lastError: null
        })
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })
}

/**
 * Get all pending outbox entries.
 */
export async function getPendingOutbox() {
    const store = await tx(STORE_OUTBOX)
    const index = store.index('status')
    return new Promise((resolve, reject) => {
        const req = index.getAll('pending')
        req.onsuccess = () => resolve(req.result || [])
        req.onerror = () => reject(req.error)
    })
}

/**
 * Mark an outbox entry as sent (removes it).
 */
export async function removeFromOutbox(id) {
    const store = await tx(STORE_OUTBOX, 'readwrite')
    return new Promise((resolve, reject) => {
        const req = store.delete(id)
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
    })
}

/**
 * Update an outbox entry (e.g. increment attempts, set error).
 */
export async function updateOutboxEntry(id, updates) {
    const store = await tx(STORE_OUTBOX, 'readwrite')
    return new Promise((resolve, reject) => {
        const getReq = store.get(id)
        getReq.onsuccess = () => {
            const entry = getReq.result
            if (!entry) return resolve(null)
            const updated = { ...entry, ...updates }
            const putReq = store.put(updated)
            putReq.onsuccess = () => resolve(updated)
            putReq.onerror = () => reject(putReq.error)
        }
        getReq.onerror = () => reject(getReq.error)
    })
}

/**
 * Count pending outbox entries.
 */
export async function getOutboxCount() {
    const store = await tx(STORE_OUTBOX)
    const index = store.index('status')
    return new Promise((resolve, reject) => {
        const req = index.count('pending')
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })
}

// ─── Reference Data Cache (stale-while-revalidate) ───────────────────────────

function cacheKey(tableName, userId) {
    return `${userId || 'public'}:${tableName}`
}

/**
 * Save reference data rows into IndexedDB for offline resilience.
 * @param {string} tableName — e.g. "arch_config"
 * @param {Array} data — rows from Supabase
 * @param {string|null} userId — Supabase user ID
 */
export async function saveReferenceCache(tableName, data, userId = null) {
    const key = cacheKey(tableName, userId)
    const store = await tx(STORE_REFERENCE, 'readwrite')
    return new Promise((resolve, reject) => {
        const req = store.put({
            key,
            tableName,
            data,
            userId: userId || null,
            cachedAt: new Date().toISOString()
        })
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
    })
}

/**
 * Load cached reference data. Returns null if none or expired.
 * @param {string} tableName
 * @param {string|null} userId
 * @param {number} maxAgeMs — default 24h
 */
export async function loadReferenceCache(tableName, userId = null, maxAgeMs = 24 * 60 * 60 * 1000) {
    const key = cacheKey(tableName, userId)
    const store = await tx(STORE_REFERENCE)
    return new Promise((resolve, reject) => {
        const req = store.get(key)
        req.onsuccess = () => {
            const result = req.result
            if (!result) return resolve(null)
            const age = Date.now() - new Date(result.cachedAt).getTime()
            if (maxAgeMs && age > maxAgeMs) return resolve(null)
            resolve({ data: result.data, cachedAt: result.cachedAt })
        }
        req.onerror = () => reject(req.error)
    })
}

/**
 * Clear cached reference data for a table.
 */
export async function clearReferenceCache(tableName, userId = null) {
    const key = cacheKey(tableName, userId)
    const store = await tx(STORE_REFERENCE, 'readwrite')
    return new Promise((resolve, reject) => {
        const req = store.delete(key)
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
    })
}
